/**
 * <s-install-app> — "Get the app" prompt for the installable storefront.
 * Chrome/Edge/Android: shows once the browser offers installation, then opens the native install prompt.
 * iPhone/iPad Safari: shows Add to Home Screen instructions instead.
 * Hidden when already installed, or for 30 days after the shopper dismisses it.
 */
const DISMISS_KEY = 'toygenie-install-dismissed';
const DISMISS_DAYS = 30;

class SInstallApp extends HTMLElement {
  #ac;
  #deferredPrompt = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const preview = this.hasAttribute('preview');

    const installButton = this.querySelector('[data-install]');
    const dismissButton = this.querySelector('[data-dismiss]');
    const iosHelp = this.querySelector('[data-ios-help]');

    if (preview) this.hidden = false;
    const persistent = this.hasAttribute('persistent');
    if (!preview && (this.#isStandalone() || (!persistent && this.#isDismissed()))) return;

    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault();
      this.#deferredPrompt = event;
      this.hidden = false;
    }, opts);

    window.addEventListener('appinstalled', () => {
      this.#deferredPrompt = null;
      this.hidden = true;
    }, opts);

    if (this.#isIos()) this.hidden = false;

    installButton?.addEventListener('click', async () => {
      if (this.#deferredPrompt) {
        // The footer link and floating pill share one browser prompt, which can only be used once per page view.
        const promptEvent = this.#deferredPrompt;
        this.#deferredPrompt = null;
        try {
          promptEvent.prompt();
          const { outcome } = await promptEvent.userChoice;
          if (outcome === 'accepted') this.hidden = true;
        } catch (e) {}
        return;
      }
      if (iosHelp) {
        iosHelp.hidden = !iosHelp.hidden;
        installButton.setAttribute('aria-expanded', String(!iosHelp.hidden));
      }
    }, opts);

    dismissButton?.addEventListener('click', () => {
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch (e) {}
      this.hidden = true;
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #isStandalone() {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  }

  #isIos() {
    const ua = window.navigator.userAgent;
    return /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && window.navigator.maxTouchPoints > 1);
  }

  #isDismissed() {
    try {
      const at = Number(localStorage.getItem(DISMISS_KEY));
      return at > 0 && Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
    } catch (e) {
      return false;
    }
  }
}

if (!customElements.get('s-install-app')) customElements.define('s-install-app', SInstallApp);
